/**
 * Top-level route table.
 *
 * Route guards live in `components/layout/RequireAuth` and
 * `components/layout/AdminRoute`. The root `/` redirects to
 * `/tickets`; Dashboard and Reports placeholders were pulled for
 * launch on 2026-05-18.
 */
import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";

import { LoginPage } from "@/pages/auth/LoginPage";
import { PasswordSetupPage } from "@/pages/auth/PasswordSetupPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { MyTicketsPage } from "@/pages/customer/MyTicketsPage";
import { NewTicketPage } from "@/pages/customer/NewTicketPage";
import { TicketDetailCustomerPage } from "@/pages/customer/TicketDetailCustomerPage";
import { ActivityPage } from "@/pages/agent/ActivityPage";
import { TasksPage } from "@/pages/agent/TasksPage";
import { AgentInboxPage } from "@/pages/agent/AgentInboxPage";
import { AgentInboxTriagePage } from "@/pages/agent/AgentInboxTriagePage";
import { TicketDetailAgentPage } from "@/pages/agent/TicketDetailAgentPage";
import { AdminAuditPage } from "@/pages/admin/AdminAuditPage";
import { AdminHealthPage } from "@/pages/admin/AdminHealthPage";
import { AdminImportStatusPage } from "@/pages/admin/AdminImportStatusPage";
import { AdminLandingPage } from "@/pages/admin/AdminLandingPage";
import { AdminLogsPage } from "@/pages/admin/AdminLogsPage";
import { AdminSecurityPage } from "@/pages/admin/AdminSecurityPage";
import { AdminSenderRewritesPage } from "@/pages/admin/AdminSenderRewritesPage";
import { AdminSettingsPage } from "@/pages/admin/AdminSettingsPage";
import { AdminSpamPage } from "@/pages/admin/AdminSpamPage";
import { AdminUsersPage } from "@/pages/admin/AdminUsersPage";
import { AdminWebhooksPage } from "@/pages/admin/AdminWebhooksPage";
import AppShell from "@/components/layout/AppShell";
import { RequireAuth } from "@/components/layout/RequireAuth";
import { AdminRoute } from "@/components/layout/AdminRoute";
import { useAuthStore } from "@/stores/auth";

function ErrorFallback({ error }: { error: unknown }): JSX.Element {
  const message =
    error instanceof Error
      ? error.message
      : "An unexpected error occurred. Please refresh and try again.";
  return (
    <div className="flex h-full items-center justify-center bg-neutral-50 p-8">
      <div className="max-w-md rounded-lg border border-red-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-red-900">
          Something went wrong
        </h2>
        <p className="mt-2 text-sm text-neutral-700">{message}</p>
      </div>
    </div>
  );
}

/**
 * TicketsListRoleRouter - renders the customer ticket list for
 * customers and the agent inbox for agents / system admins.
 *
 * SPEC §9 states agents have no customer-portal view; the assumption
 * is Buyist staff have no personal customer tickets. The role on the
 * auth store decides which page mounts.
 */
function TicketsListRoleRouter(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  if (role === "agent" || role === "system_admin") {
    return <AgentInboxPage />;
  }
  return <MyTicketsPage />;
}

/**
 * AgentOnlyRoute - guards routes that have no customer equivalent
 * (/activity, added 2026-07-30; /tasks, added 2026-08-08). A
 * customer who lands here -
 * by typing the URL or following a stale link - goes to their own
 * ticket list rather than seeing an error, because from their side the
 * page simply does not exist. Mirrors the redirect the inbox page does
 * for itself; the backend refuses these endpoints for customers too,
 * so this is UX, not the security boundary.
 */
function AgentOnlyRoute(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  if (role === "agent" || role === "system_admin") {
    return <Outlet />;
  }
  return <Navigate to="/tickets" replace />;
}

/** Same logic, but for the /tickets/:code detail route. */
function TicketDetailRoleRouter(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  if (role === "agent" || role === "system_admin") {
    return <TicketDetailAgentPage />;
  }
  return <TicketDetailCustomerPage />;
}

/**
 * FreshdeskTicketRedirect - one-way redirect from the legacy Freshdesk
 * URL pattern to ours. Customers and agents both have years of
 * accumulated bookmarks, notification emails, and chat history pointing
 * at ``support.buyist.com/a/tickets/{n}``; after cutover those URLs
 * arrive at our domain instead. This route catches them and Navigates
 * to ``/tickets/{code}``. RequireAuth on the destination handles login
 * if the visitor is signed out (and preserves the destination via the
 * ?redirect= query param, so the user lands on the ticket after
 * authenticating).
 *
 * The numeric ID coming off the FD URL is passed through verbatim -
 * the backend's get_ticket_by_public_code resolver handles the bare-
 * numeric form as a back-compat path, so /a/tickets/104944 -> our
 * /tickets/104944 still loads the right ticket. The detail page then
 * canonicalizes the URL bar to the public_code form (e.g. F-104944)
 * once the ticket has loaded.
 */
function FreshdeskTicketRedirect(): JSX.Element {
  const { number } = useParams<{ number: string }>();
  return (
    <Navigate to={number ? `/tickets/${number}` : "/tickets"} replace />
  );
}

export default function App(): JSX.Element {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Routes>
        {/* Public auth routes. */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/password-setup" element={<PasswordSetupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Legacy Freshdesk URL compatibility. Customers + agents have
            years of bookmarks and emails pointing at the FD URL
            shapes; cutover should not break those links. Three FD
            patterns are covered:
              - /a/tickets/{n}        - FD agent admin URL
              - /helpdesk/tickets/{n} - FD customer portal URL
              - /support/tickets/{n}  - FD alternate customer portal URL
            All three Navigate to /tickets/{n} where RequireAuth +
            the role-router pick the right page for the viewer. The
            redirect runs BEFORE RequireAuth so an unauthenticated
            visitor still lands on the right ticket after login
            (the destination preserves the URL through the auth
            flow via the standard ?redirect= query param). */}
        <Route
          path="/a/tickets/:number"
          element={<FreshdeskTicketRedirect />}
        />
        <Route path="/a/tickets" element={<Navigate to="/tickets" replace />} />
        <Route
          path="/helpdesk/tickets/:number"
          element={<FreshdeskTicketRedirect />}
        />
        <Route
          path="/helpdesk/tickets"
          element={<Navigate to="/tickets" replace />}
        />
        <Route
          path="/support/tickets/:number"
          element={<FreshdeskTicketRedirect />}
        />
        <Route
          path="/support/tickets"
          element={<Navigate to="/tickets" replace />}
        />

        {/* Authenticated routes wrapped in the AppShell. */}
        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            {/* Root redirects to /tickets. Dashboard and Reports
                were pulled for launch on 2026-05-18 (placeholders
                only). */}
            <Route path="/" element={<Navigate to="/tickets" replace />} />
            <Route path="/tickets" element={<TicketsListRoleRouter />} />
            <Route path="/tickets/new" element={<NewTicketPage />} />
            <Route path="/inbox" element={<AgentInboxTriagePage />} />

            {/* Agent-only surfaces with no customer counterpart. */}
            <Route element={<AgentOnlyRoute />}>
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/activity" element={<ActivityPage />} />
            </Route>
            <Route
              path="/tickets/:code"
              element={<TicketDetailRoleRouter />}
            />
            <Route path="/settings" element={<SettingsPage />} />

            <Route element={<AdminRoute />}>
              <Route path="/admin" element={<AdminLandingPage />} />
              <Route path="/admin/settings" element={<AdminSettingsPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/logs" element={<AdminLogsPage />} />
              <Route path="/admin/audit" element={<AdminAuditPage />} />
              <Route
                path="/admin/security"
                element={<AdminSecurityPage />}
              />
              <Route
                path="/admin/import-status"
                element={<AdminImportStatusPage />}
              />
              <Route path="/admin/health" element={<AdminHealthPage />} />
              <Route path="/admin/spam" element={<AdminSpamPage />} />
              <Route
                path="/admin/sender-rewrites"
                element={<AdminSenderRewritesPage />}
              />
              <Route path="/admin/webhooks" element={<AdminWebhooksPage />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/tickets" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}
